import type { NavConfig } from '@pressreader/navigation-types';
import { isArticleNavConfig, isTextOrPageViewNavConfig, type TextOrPageViewNavConfig } from '@pressreader/views-navigation';

/**
 * The language the navigation on screen asserts for the issue `issueKey`: a language code, `null` for the issue
 * shown in the original, `undefined` when nothing on screen asserts one — an entry into the issue then shows it in
 * the original.
 *
 * Sources: an Article view opened from an issue view of the issue (`openingContext`) — a fullscreen expansion
 * carries the host's opening context; the view it collapses back to (`returnTo`) is a navigation target, not a
 * language source, so an article read inside the expansion (a related article) keeps the collapse target while
 * asserting nothing — and a TextView or PageView of the issue on screen, with an article popup over it. An Article
 * view without an issue-view provenance — a deep link, a search hit, a related article — and any view of another
 * issue assert nothing.
 */
export function resolveIssueLanguageAssertedByNavConfig({
    navConfig,
    issueKey,
}: {
    navConfig: NavConfig | null | undefined;
    issueKey: string;
}): TextOrPageViewNavConfig['language'] {
    const config = navConfig ?? null;
    if (isArticleNavConfig(config)) {
        const { openingContext } = config;
        if (openingContext?.issueKey === issueKey) {
            return openingContext.language;
        }

        return undefined;
    }

    return isTextOrPageViewNavConfig(config) && config.issueKey === issueKey ? config.language : undefined;
}
